import { useState, useCallback, useRef, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function makeSession(url = '', ind = '') {
  return {
    id: crypto.randomUUID(),
    url: url || '',
    ind: ind || '',
    comps: ['', '', '', '', ''],
    stage: 0,
    done: [],
    topics: null,
    gap_analysis_raw: '',
    topic: null,
    brief: '',
    draft: '',
    pol: '',
    expert: '',
    label: url || 'New session',
    completed_articles: [],
    created_at: new Date().toISOString(),
  };
}

export function useSessions() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState('idle');
  const saveTimers = useRef({});
  const hasBootstrappedRef = useRef(false);

  /* Load sessions from backend on mount */
  useEffect(() => {
    // Guard against React StrictMode double-invocation
    if (hasBootstrappedRef.current) return;
    hasBootstrappedRef.current = true;

    const loadSessions = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${BACKEND_URL}/api/sessions`, {
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Failed to load sessions');
        }

        const data = await response.json();

        if (data.length > 0) {
          setSessions(data);
          setActiveId(data[0].id);
        } else {
          // Create first session
          const fresh = makeSession();
          setSessions([fresh]);
          setActiveId(fresh.id);

          // Save to backend with client-generated id
          await fetch(`${BACKEND_URL}/api/sessions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ id: fresh.id, url: fresh.url, ind: fresh.ind })
          });
        }
      } catch (error) {
        console.error('Error loading sessions:', error);
        // Create a local session as fallback
        const fresh = makeSession();
        setSessions([fresh]);
        setActiveId(fresh.id);
      } finally {
        setLoading(false);
      }
    };

    loadSessions();
  }, []);

  /* Debounced save to backend */
  const scheduleSave = useCallback((session) => {
    if (saveTimers.current[session.id]) {
      clearTimeout(saveTimers.current[session.id]);
    }

    setSyncStatus('saving');

    saveTimers.current[session.id] = setTimeout(async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/sessions/${session.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(session)
        });

        if (!response.ok) {
          throw new Error('Failed to save session');
        }

        setSyncStatus('saved');
        setTimeout(() => setSyncStatus('idle'), 2000);
      } catch (error) {
        console.error('Save error:', error);
        setSyncStatus('error');
      }
    }, 800); // 800ms debounce
  }, []);

  /* Add session */
  const addSession = useCallback(async (url = '', ind = '') => {
    const s = makeSession(url, ind);
    setSessions((prev) => [...prev, s]);
    setActiveId(s.id);

    try {
      await fetch(`${BACKEND_URL}/api/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id: s.id, url, ind })
      });
    } catch (error) {
      console.error('Error creating session:', error);
    }

    return s;
  }, []);

  /* Remove session */
  const removeSession = useCallback(async (id) => {
    // Delete from backend first
    try {
      await fetch(`${BACKEND_URL}/api/sessions/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Error deleting session:', error);
    }

    setSessions((prev) => {
      const next = prev.filter((s) => s.id !== id);

      // If that was the last session, create a fresh empty one so the UI is never empty
      if (next.length === 0) {
        const fresh = makeSession();
        // Persist the new session
        fetch(`${BACKEND_URL}/api/sessions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ id: fresh.id, url: fresh.url, ind: fresh.ind })
        }).catch((err) => console.error('Auto-create session error:', err));
        setActiveId(fresh.id);
        return [fresh];
      }

      setActiveId((cur) => (cur === id ? next[next.length - 1].id : cur));
      return next;
    });
  }, []);

  /* Duplicate session */
  const duplicateSession = useCallback(async (id) => {
    setSessions((prev) => {
      const src = prev.find((s) => s.id === id);
      if (!src) return prev;

      const clone = {
        ...src,
        id: crypto.randomUUID(),
        label: `${src.label} (copy)`,
        created_at: new Date().toISOString(),
      };

      // Save to backend
      fetch(`${BACKEND_URL}/api/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(clone)
      }).catch((err) => console.error('Duplicate error:', err));

      setActiveId(clone.id);
      return [...prev, clone];
    });
  }, []);

  /* Update session (auto-saves to backend) */
  const updateSession = useCallback(
    (id, patch) => {
      setSessions((prev) => {
        const next = prev.map((s) => {
          if (s.id !== id) return s;
          const updated = { ...s, ...patch };
          scheduleSave(updated);
          return updated;
        });
        return next;
      });
    },
    [scheduleSave]
  );

  const activeSession = sessions.find((s) => s.id === activeId) || null;

  return {
    sessions,
    activeId,
    activeSession,
    setActiveId,
    addSession,
    removeSession,
    duplicateSession,
    updateSession,
    loading,
    syncStatus,
  };
}

export { makeSession };
